import { useEffect } from 'react';
import useApi from './useApi';
import { useRotationStore } from '../context/RotationContext';
import RotationFactory from '../factory/ReportEvents/Rotation/RotationFactory';
import { RotationBM } from '../types/BusinessModel/RotationBM';
import { PortCall } from '../types/ViewModel/PortCall';
import { newSeaPortCall } from '../factory/ReportEvents/Rotation/PortCallFactory';
import useApiEndpoints from '../context/useApiEndpoints';
import { buildQueryParams } from './endpoints/queryParamsHelper';
import { CargoListItemBM } from '../types/BusinessModel/CargoListItemBM';
import { Rotation } from '../types/ViewModel/Rotation';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import { useStowageStore } from 'src/context/StowageContext';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import useAppFeature from 'src/app/useAppFeature';
import { getDateNow, isDateBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import { useVesselStore } from 'src/context/VesselContext';
import { updateRotationWithStowagePlanData } from 'src/utils/stowage/integrations/rotation/stowagePlanUpdates';
import { mergeRotationWithStowagePlan } from 'src/utils/stowage/integrations/rotation/rotationMergeUtils';
import { TimeZoneDataBM } from 'src/types/BusinessModel/TimeZoneDataBM';
import { TimeZoneFactory } from 'src/factory/TimeZoneFactory';
import { TimeZoneData } from 'src/types/ViewModel/TimeZoneData';
import toast from 'react-hot-toast';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { processApiEndpointError } = apiEndpointUtils();
    const { state, getApiData } = useApi();
    const { setRotation, setCargoList, portNameList, setTimeZoneData } = useRotationStore();
    const { loadedMasterStowagePlan } = useStowageStore();
    const { newRotation, mapCargoList } = RotationFactory();
    const { isFeatureEnabled } = useAppFeature();
    const { selectedVessel } = useVesselStore();

    const getExtendedRotation = async (imoNumber: number) => {
        let result = null;
        // Avoid calling legacy rotation API for vessels that started using Orca Web after Dec 1, 2025. They will not have data from Orca (legacy) system.
        if (!selectedVessel.orcaWebStartDate || isDateBefore(selectedVessel.orcaWebStartDate, new Date(2025, 11, 1), 'day')) {
            result = await getApiData({
                endpoint: apiEndpoints['getExtendedRotation'].url,
                queryParam: buildQueryParams({ imoNumber }),
            });
        }

        let rotation: Rotation | null = null;

        if (result) {
            const rotationBM = result as RotationBM;
            rotation = newRotation(rotationBM);
        } else if (isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber, date: getDateNow() }) && loadedMasterStowagePlan) {
            // New vessel scenario: No legacy rotation data exists, create empty rotation
            // to be populated entirely from the OW stowage plan
            rotation = new Rotation(
                [],
                loadedMasterStowagePlan.lastUpdatedUtc,
                loadedMasterStowagePlan.title,
                loadedMasterStowagePlan.syncedWithImosDateTimeUtc ?? new Date(),
                loadedMasterStowagePlan.lastUpdatedUtc
            );
        }

        if (!rotation) {
            // No data available from either source - master plan may not be loaded yet
            return;
        }

        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber, date: getDateNow() }) && loadedMasterStowagePlan) {
            updateRotationWithStowagePlanData(rotation, loadedMasterStowagePlan);
            // Get all distinct port IDs from the master plan rotation
            const portIds = Array.from(new Set(loadedMasterStowagePlan.rotation.map((portCall) => portCall.portId)));

            if (portIds.length === 0) {
                toast.error('No port IDs found in the stowage plan.');
            } else {
                const timeZoneData = await getTimeZoneData(portIds);
                if (!timeZoneData) {
                    toast.error('Failed to load time zone data for the ports in the stowage plan.');
                } else {
                    const filteredPortCallIds = mergeRotationWithStowagePlan(
                        rotation,
                        loadedMasterStowagePlan.rotation,
                        selectedVessel.firstVoyageOrcaWebStowageImosSync,
                        portNameList,
                        timeZoneData
                    );
                    // Store filtered port call IDs so we don't create deleted port calls for them
                    rotation.filteredPortCallIds = filteredPortCallIds;
                }
            }
        }
        return processPortCalls(rotation);
    };

    const getCargoList = (imoNumber: number) => {
        getApiData({
            endpoint: apiEndpoints['getCargoList'].url,
            queryParam: buildQueryParams({ imoNumber }),
        });
    };

    const getTimeZoneData = async (portIds: number[]): Promise<TimeZoneData | undefined> => {
        const result = await getApiData({
            endpoint: apiEndpoints['getTimeZoneData'].url,
            queryParam: buildQueryParams({ portIds }),
        });

        if (result) {
            const timeZoneDataBM = result as TimeZoneDataBM;
            const timeZoneFactory = new TimeZoneFactory();
            const timeZoneData = timeZoneFactory.newTimeZoneData(timeZoneDataBM);

            setTimeZoneData(timeZoneData);
            return timeZoneData;
        }
    };

    const processPortCalls = (rotation: Rotation) => {
        if (!rotation) return;
        const processedPortCallsArray: PortCall[] = [];
        rotation.portCallArray.forEach((portCall) => {
            processedPortCallsArray.push(portCall);
            processedPortCallsArray.push(newSeaPortCall(portCall));
        });
        rotation.portCallArray = processedPortCallsArray;
        setRotation({ ...rotation });
        return rotation;
    };

    useEffect(() => {
        if (state.status === 'success') {
            if (state.endpoint === apiEndpoints['getCargoList'].url) {
                const cargoListBM = state.result as unknown as CargoListItemBM[];
                const cargoList = mapCargoList(cargoListBM);
                setCargoList(cargoList);
            }
        } else {
            processApiEndpointError(state);
        }
    }, [state]);

    return { state, getExtendedRotation, getCargoList, getTimeZoneData };
};
